import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { AppActions } from "../../../../store/ducks/app";
import { DashBoardActions } from "../../../../store/ducks/dashboard";
import { AtribuirActions } from "../../../../store/ducks/atribuir";
import { AuthActions } from "../../../../store/ducks/auth";
import { handlePaciente } from "../../../../utils/funcs";
import {
	CopilotPatientReference,
	CopilotSummaryData,
	fetchCopilotEvolution,
	fetchCopilotSummary,
	fetchFullPatientForDashboard,
	isAbortError,
} from "../../services/clinicalDashboardService";

export interface AICopilotPatient {
	name: string;
	photoUrl: string | null;
	initials: string;
	evaluationDate: string;
}

interface AsyncContent<T> {
	data: T | null;
	loading: boolean;
	error: string | null;
}

export interface CopilotSummarySnapshot
	extends AsyncContent<CopilotSummaryData> {
	patientId: number | null;
	retry: () => void;
}

const emptyAsyncContent = <T>(): AsyncContent<T> => ({
	data: null,
	loading: false,
	error: null,
});

export const useAICopilotViewModel = (
	externalPatient: CopilotPatientReference | null,
	onOpenRecord: () => void,
	onSummaryStateChange?: (snapshot: CopilotSummarySnapshot) => void,
	onRefreshLatestPatient?: () => CopilotPatientReference | null
) => {
	const dispatch = useDispatch();
	const token = useSelector((state: any) => state?.auth?.user?.token) || "";
	const configServices = useSelector(
		(state: any) => state?.configs?.config_servicos
	);
	const [activeTab, setActiveTab] = useState<"resumo" | "evolucao" | "atencao">(
		"atencao"
	);
	const [summary, setSummary] = useState<AsyncContent<CopilotSummaryData>>(
		emptyAsyncContent()
	);
	const [summaryPatientId, setSummaryPatientId] = useState<number | null>(null);
	const [evolution, setEvolution] = useState<AsyncContent<any>>(
		emptyAsyncContent()
	);
	const [sendingReport, setSendingReport] = useState(false);
	const [reportError, setReportError] = useState<string | null>(null);
	const summaryRequest = useRef<AbortController | null>(null);
	const evolutionRequest = useRef<AbortController | null>(null);
	const reportRequest = useRef<AbortController | null>(null);
	const summarySequence = useRef(0);
	const evolutionSequence = useRef(0);

	const loadSummary = useCallback(async () => {
		if (!externalPatient?.id || !token) return;
		const patientId = externalPatient.id;
		summaryRequest.current?.abort();
		const controller = new AbortController();
		summaryRequest.current = controller;
		const sequence = ++summarySequence.current;
		setSummaryPatientId(patientId);
		setSummary((current) => ({ ...current, loading: true, error: null }));
		try {
			const data = await fetchCopilotSummary(
				token,
				patientId,
				controller.signal
			);
			if (sequence === summarySequence.current) {
				setSummary({ data, loading: false, error: null });
			}
		} catch (error) {
			if (!isAbortError(error) && sequence === summarySequence.current) {
				if ((error as any)?.status === 401) {
					dispatch(AuthActions.auth_logout());
				}
				setSummary({
					data: null,
					loading: false,
					error:
						(error as Error)?.message ||
						"Não foi possível carregar os pontos de atenção.",
				});
			}
		}
	}, [externalPatient?.id, token, dispatch]);

	const loadEvolution = useCallback(async () => {
		if (!externalPatient?.id || !token) return;
		evolutionRequest.current?.abort();
		const controller = new AbortController();
		evolutionRequest.current = controller;
		const sequence = ++evolutionSequence.current;
		setEvolution((current) => ({ ...current, loading: true, error: null }));
		try {
			const data = await fetchCopilotEvolution(
				token,
				externalPatient.id,
				controller.signal
			);
			if (sequence === evolutionSequence.current) {
				setEvolution({ data, loading: false, error: null });
			}
		} catch (error) {
			if (!isAbortError(error) && sequence === evolutionSequence.current) {
				if ((error as any)?.status === 401) {
					dispatch(AuthActions.auth_logout());
				}
				setEvolution({
					data: null,
					loading: false,
					error:
						(error as Error)?.message ||
						"Não foi possível carregar a evolução.",
				});
			}
		}
	}, [externalPatient?.id, token]);

	useEffect(() => {
		summarySequence.current += 1;
		evolutionSequence.current += 1;
		summaryRequest.current?.abort();
		evolutionRequest.current?.abort();
		reportRequest.current?.abort();
		setSendingReport(false);
		setActiveTab("atencao");
		setSummaryPatientId(externalPatient?.id || null);
		setSummary(emptyAsyncContent());
		setEvolution(emptyAsyncContent());
		setReportError(null);
		if (externalPatient?.id) loadSummary();
		return () => {
			summarySequence.current += 1;
			evolutionSequence.current += 1;
			summaryRequest.current?.abort();
			evolutionRequest.current?.abort();
		};
	}, [externalPatient?.id, loadSummary]);

	useEffect(() => {
		onSummaryStateChange?.({
			patientId: summaryPatientId,
			data: summary.data,
			loading: summary.loading,
			error: summary.error,
			retry: loadSummary,
		});
	}, [
		summaryPatientId,
		summary.data,
		summary.loading,
		summary.error,
		loadSummary,
		onSummaryStateChange,
	]);

	useEffect(
		() => () => {
			reportRequest.current?.abort();
		},
		[]
	);

	const patient = useMemo<AICopilotPatient | null>(() => {
		if (!externalPatient) return null;
		const name = externalPatient.nome || "";
		const names = name.trim().split(/\s+/).filter(Boolean);
		return {
			name,
			photoUrl: externalPatient.foto || null,
			initials:
				names.length > 1
					? `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase()
					: name.charAt(0).toUpperCase(),
			evaluationDate:
				externalPatient.realizadaEm || summary.data?.ULTIMA_AVALIACAO
					? moment(
							externalPatient.realizadaEm || summary.data?.ULTIMA_AVALIACAO
					  ).format("DD/MM/YY")
					: "sem avaliação recente",
		};
	}, [externalPatient, summary.data]);

	const activeContent = activeTab === "evolucao" ? evolution : summary;
	const summaryText =
		activeTab === "evolucao"
			? evolution.data?.texto || ""
			: summary.data?.RESUMO_TXT?.conteudo || "";
	const summaryTitle =
		activeTab === "evolucao"
			? "Evolução"
			: activeTab === "resumo"
			? "Resumo do Paciente"
			: summary.data?.RESUMO_TXT?.titulo || "Pontos de atenção";

	const handleSendReport = async () => {
		if (!externalPatient?.id || !token || sendingReport) return;
		reportRequest.current?.abort();
		const controller = new AbortController();
		reportRequest.current = controller;
		setSendingReport(true);
		setReportError(null);
		try {
			const fullPatient = await fetchFullPatientForDashboard(
				token,
				externalPatient.id,
				controller.signal
			);
			if (!fullPatient) {
				throw new Error(
					"Não foi possível carregar o cadastro completo do paciente."
				);
			}
			handlePaciente(fullPatient, dispatch, configServices);
			dispatch(AtribuirActions.set_menu_option_selected(""));
			dispatch(AppActions.set_lista_pacientes([]));
			dispatch(DashBoardActions.set_current_page("emissao_relatorios"));
		} catch (error) {
			if (!isAbortError(error)) {
				if ((error as any)?.status === 401) {
					dispatch(AuthActions.auth_logout());
				}
				setReportError(
					(error as Error)?.message || "Não foi possível abrir o relatório."
				);
			}
		} finally {
			if (!controller.signal.aborted) setSendingReport(false);
		}
	};

	const handleViewEvolution = () => {
		setActiveTab("evolucao");
		if (!evolution.data && !evolution.loading) loadEvolution();
	};

	const handleViewSummary = () => {
		setActiveTab("resumo");
		if (!summary.data && !summary.loading && !summary.error) loadSummary();
	};

	const handleViewAttentionPoints = () => {
		setActiveTab("atencao");
		if (!summary.data && !summary.loading && !summary.error) loadSummary();
	};

	const handleRefreshLatestPatient = () => {
		const latestPatient = onRefreshLatestPatient?.() || null;
		setActiveTab("atencao");
		if (!latestPatient || latestPatient.id === externalPatient?.id) {
			loadSummary();
		}
	};

	return {
		activeTab,
		patient,
		summaryText,
		summaryTitle,
		contentLoading: activeContent.loading,
		contentError: activeContent.error,
		contentEmpty:
			!activeContent.loading && !activeContent.error && !summaryText,
		sendingReport,
		reportError,
		handleSendReport,
		handleViewSummary,
		handleOpenRecord: onOpenRecord,
		handleViewEvolution,
		handleViewAttentionPoints,
		handleRefreshSummary: handleRefreshLatestPatient,
		handleRetryContent: activeTab === "evolucao" ? loadEvolution : loadSummary,
		handleStartFirstEvaluation: () =>
			dispatch(AppActions.set_modal_adicionar_paciente(true)),
	};
};
